import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { sendLeadToGrowthHub } from "@/lib/sendLeadToGrowthHub";
import { Send } from "lucide-react";

/** Two-field (name + phone) callback request. Lowest-friction lead capture. */
const QuickCallbackForm = ({ source, onDone, compact = false }: { source: string; onDone?: () => void; compact?: boolean }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();

  // Returning visitors: prefill from details they previously submitted (stored only in their own browser).
  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem("obc_saved_contact") || "null");
      if (saved) {
        if (saved.name) setName(String(saved.name).slice(0, 80));
        if (saved.phone) setPhone(String(saved.phone).slice(0, 15));
      }
    } catch { /* ignore */ }
  }, []);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const n = name.trim().slice(0, 100);
    const p = phone.replace(/[^\d+]/g, "").slice(0, 16);
    if (!n || p.replace(/\D/g, "").length < 10) {
      toast({ title: "Check your details", description: "Please enter your name and a 10-digit phone number.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await supabase.from("crm_leads").insert({ name: n, phone: p, email: "not-provided@placeholder.com", source_page: `${source}:${location.pathname}` });
      await sendLeadToGrowthHub({ name: n, phone: p, email: "", form_type: source });
      try {
        const prev = JSON.parse(localStorage.getItem("obc_saved_contact") || "null") || {};
        localStorage.setItem("obc_saved_contact", JSON.stringify({ ...prev, name: n, phone: p }));
      } catch { /* ignore */ }
      toast({ title: "Request received", description: "We'll call you back as soon as we can." });
      setName(""); setPhone("");
      onDone?.();
    } catch {
      toast({ title: "Something went wrong", description: "Please try again or call us.", variant: "destructive" });
    }
    setLoading(false);
  };

  return (
    <form onSubmit={submit} name={source} autoComplete="on" className={compact ? "flex items-center gap-3 justify-center" : "space-y-3"}>
      <Input id={`${source}-name`} name="name" placeholder="Your name" autoComplete="name" autoCapitalize="words" value={name} onChange={(e) => setName(e.target.value)} required maxLength={100}
        className={compact ? "max-w-[170px] h-9 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60" : "h-12 text-base"} />
      <Input id={`${source}-tel`} name="tel" placeholder="Phone number" type="tel" inputMode="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} required maxLength={20}
        className={compact ? "max-w-[170px] h-9 bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground placeholder:text-primary-foreground/60" : "h-12 text-base"} />
      <Button type="submit" variant={compact ? "secondary" : "accent"} size={compact ? "sm" : "lg"} disabled={loading} className={compact ? "" : "w-full"}>
        <Send className="h-4 w-4 mr-1" />
        {loading ? "Sending..." : "Call Me Back"}
      </Button>
    </form>
  );
};

export default QuickCallbackForm;
